'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import DMSShell from '@/components/DMSShell';
import {
  ChevronLeftIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

interface FloorplanTerms {
  advanceRate: number;
  apr: number;
  setupFee: number;
  curtailmentCycleDays: number;
  curtailmentFeePercent: number;
  curtailmentFeeMin: number;
  gracePeriodDays: number;
  lenderName: string;
  accountNumber: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
}

const defaultTerms: FloorplanTerms = {
  advanceRate: 90,
  apr: 8.5,
  setupFee: 150,
  curtailmentCycleDays: 90,
  curtailmentFeePercent: 10,
  curtailmentFeeMin: 50,
  gracePeriodDays: 5,
  lenderName: 'NextGear Capital',
  accountNumber: 'NG-00123456',
  contactName: 'Lisa Fontaine',
  contactPhone: '(800) 555-0444',
  contactEmail: 'lfontaine@nextgear.com',
};

function InfoTip({ text }: { text: string }) {
  return (
    <span title={text} style={{ cursor: 'help', marginLeft: 4, verticalAlign: 'middle' }}>
      <InformationCircleIcon style={{ width: 13, height: 13, color: 'var(--steel)', display: 'inline' }} />
    </span>
  );
}

export default function FloorplanTermsPage() {
  const [terms, setTerms] = useState<FloorplanTerms>(defaultTerms);
  const [saved, setSaved] = useState(false);

  function update(field: keyof FloorplanTerms, value: string | number) {
    setTerms(prev => ({ ...prev, [field]: value }));
  }

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  // Live projection: cost on a $20,000 vehicle over curtailment cycle
  const vehicleValue = 20000;
  const advanceAmount = vehicleValue * (terms.advanceRate / 100);
  const dailyInterest = advanceAmount * (terms.apr / 100) / 365;
  const cycleInterest = dailyInterest * terms.curtailmentCycleDays;
  const curtailmentFee = Math.max(advanceAmount * (terms.curtailmentFeePercent / 100), terms.curtailmentFeeMin);

  return (
    <DMSShell>
      <div className="page-header" style={{ paddingBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link href="/settings" style={{ textDecoration: 'none' }}>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--steel)', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0', fontFamily: 'inherit', fontSize: 13 }}>
                <ChevronLeftIcon style={{ width: 14, height: 14 }} />
                Settings
              </button>
            </Link>
            <span style={{ color: 'var(--steel-light)' }}>/</span>
            <h1 className="page-title">Floorplan Terms</h1>
          </div>
          <button className="btn-primary" onClick={handleSave} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {saved ? <><CheckCircleIcon style={{ width: 14, height: 14 }} /> Saved!</> : 'Save Changes'}
          </button>
        </div>
      </div>

      <div style={{ padding: '0 28px 40px', maxWidth: 680, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--steel)' }}>
          Configure your floorplan line terms. These are used to calculate flooring costs in the Inventory Expenses tab and the Floorplan Projection calculator.
        </div>

        {/* Lender Info */}
        <div className="card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <CurrencyDollarIcon style={{ width: 16, height: 16, color: 'var(--accent)' }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink-heading)' }}>Floorplan Lender</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Lender Name', field: 'lenderName' as keyof FloorplanTerms },
              { label: 'Account Number', field: 'accountNumber' as keyof FloorplanTerms },
              { label: 'Contact Name', field: 'contactName' as keyof FloorplanTerms },
              { label: 'Contact Phone', field: 'contactPhone' as keyof FloorplanTerms },
              { label: 'Contact Email', field: 'contactEmail' as keyof FloorplanTerms },
            ].map(f => (
              <div key={f.field} style={f.field === 'contactEmail' ? { gridColumn: '1 / -1' } : {}}>
                <div className="field-label">{f.label}</div>
                <input
                  type="text"
                  value={terms[f.field] as string}
                  onChange={e => update(f.field, e.target.value)}
                  className="field-input"
                  style={{ fontSize: 13 }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Rate Terms */}
        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink-heading)', marginBottom: 14 }}>Rate & Advance Terms</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div className="field-label">
                Advance Rate (%)
                <InfoTip text="Percentage of vehicle value the lender will advance. E.g. 90% on a $20,000 vehicle = $18,000 floored." />
              </div>
              <input type="number" value={terms.advanceRate} onChange={e => update('advanceRate', Number(e.target.value))} className="field-input" style={{ fontSize: 13 }} />
            </div>
            <div>
              <div className="field-label">
                Annual Percentage Rate (%)
                <InfoTip text="Annual interest rate charged on the floored amount." />
              </div>
              <input type="number" step="0.01" value={terms.apr} onChange={e => update('apr', Number(e.target.value))} className="field-input" style={{ fontSize: 13 }} />
            </div>
            <div>
              <div className="field-label">
                Setup / Origination Fee ($)
                <InfoTip text="One-time fee charged when a vehicle is added to the floorplan." />
              </div>
              <input type="number" value={terms.setupFee} onChange={e => update('setupFee', Number(e.target.value))} className="field-input" style={{ fontSize: 13 }} />
            </div>
            <div>
              <div className="field-label">
                Grace Period (days)
                <InfoTip text="Days after a vehicle sells before payoff is required." />
              </div>
              <input type="number" value={terms.gracePeriodDays} onChange={e => update('gracePeriodDays', Number(e.target.value))} className="field-input" style={{ fontSize: 13 }} />
            </div>
          </div>
        </div>

        {/* Curtailment */}
        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink-heading)', marginBottom: 14 }}>Curtailment Schedule</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div className="field-label">
                Curtailment Cycle (days)
                <InfoTip text="How often a curtailment payment is required on aging inventory." />
              </div>
              <input type="number" value={terms.curtailmentCycleDays} onChange={e => update('curtailmentCycleDays', Number(e.target.value))} className="field-input" style={{ fontSize: 13 }} />
            </div>
            <div>
              <div className="field-label">
                Curtailment Fee (% of advance)
                <InfoTip text="Percentage of the advanced amount due at each curtailment." />
              </div>
              <input type="number" step="0.1" value={terms.curtailmentFeePercent} onChange={e => update('curtailmentFeePercent', Number(e.target.value))} className="field-input" style={{ fontSize: 13 }} />
            </div>
            <div>
              <div className="field-label">
                Curtailment Minimum ($)
                <InfoTip text="Minimum curtailment payment regardless of percentage." />
              </div>
              <input type="number" value={terms.curtailmentFeeMin} onChange={e => update('curtailmentFeeMin', Number(e.target.value))} className="field-input" style={{ fontSize: 13 }} />
            </div>
          </div>
        </div>

        {/* Live projection */}
        <div className="card" style={{ padding: 18, background: 'var(--cream)', border: '1px solid var(--steel-light)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--ink-heading)', marginBottom: 12 }}>
            📊 Sample Cost Projection — $20,000 Vehicle
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
            {[
              { label: 'Amount Floored', value: `$${advanceAmount.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` },
              { label: `Interest (${terms.curtailmentCycleDays}d)`, value: `$${cycleInterest.toFixed(2)}` },
              { label: 'Setup Fee', value: `$${terms.setupFee.toFixed(2)}` },
              { label: `Curtailment (${terms.curtailmentCycleDays}d)`, value: `$${curtailmentFee.toFixed(2)}` },
            ].map(stat => (
              <div key={stat.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-heading)' }}>{stat.value}</div>
                <div style={{ fontSize: 11, color: 'var(--steel)', marginTop: 2 }}>{stat.label}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--steel)', marginTop: 12, borderTop: '1px solid var(--steel-light)', paddingTop: 8 }}>
            Projection based on current terms. Actual costs may vary. Consult your floorplan agreement.
          </div>
        </div>
      </div>
    </DMSShell>
  );
}
